/**
 * Pure selection model for PV06 (SE-8316, PRD-3.7 – 3.10): players selected
 * per source list, in the order the lists were first touched. A player in two
 * selected lists counts once ("30 selected · 28 unique") — the first source
 * wins — and the bar copy is derived here so every surface says the same thing.
 * @module features/scouting/utils/watchlistSelection
 */

import { canEditWatchlistMembership } from "@/features/watchlist/constants";

import type { UserWatchlistRole } from "@/generated/graphql";

/** Players selected from one list. */
export interface SelectionSlice {
  /** Source watchlist id. */
  readonly watchlistId: string;
  /** Source list name at selection time (bar copy). */
  readonly listName: string;
  /** Selected player ids, in selection order. */
  readonly playerIds: readonly string[];
}

/** The whole live selection, slices in encounter order. */
export interface SelectionState {
  readonly slices: readonly SelectionSlice[];
}

/** Nothing selected. */
export const EMPTY_SELECTION: SelectionState = { slices: [] };

/** Header checkbox state for the rows on screen. */
export type SelectionHeaderState = "none" | "some" | "all";

const sliceFor = (
  state: SelectionState,
  watchlistId: string
): SelectionSlice | undefined =>
  state.slices.find(slice => slice.watchlistId === watchlistId);

const withSlice = (
  state: SelectionState,
  watchlistId: string,
  listName: string,
  playerIds: readonly string[]
): SelectionState => {
  const others = state.slices.filter(
    slice => slice.watchlistId !== watchlistId
  );
  if (playerIds.length === 0) return { slices: others };
  const existing = sliceFor(state, watchlistId);
  const next: SelectionSlice = { watchlistId, listName, playerIds };
  if (!existing) return { slices: [...state.slices, next] };
  return {
    slices: state.slices.map(slice =>
      slice.watchlistId === watchlistId ? next : slice
    ),
  };
};

/**
 * Selected player ids for one list.
 * @param state - Live selection
 * @param watchlistId - Source list
 * @returns The list's selected ids, in selection order.
 */
export const selectedIn = (
  state: SelectionState,
  watchlistId: string
): readonly string[] => sliceFor(state, watchlistId)?.playerIds ?? [];

/**
 * Toggles one player in one list's slice.
 * @param state - Live selection
 * @param watchlistId - Source list
 * @param listName - Source list name
 * @param playerId - Player to toggle
 * @returns The next selection.
 */
export const togglePlayer = (
  state: SelectionState,
  watchlistId: string,
  listName: string,
  playerId: string
): SelectionState => {
  const current = selectedIn(state, watchlistId);
  const next = current.includes(playerId)
    ? current.filter(id => id !== playerId)
    : [...current, playerId];
  return withSlice(state, watchlistId, listName, next);
};

/**
 * Replaces one list's slice (select all / top N / picker tick).
 * @param state - Live selection
 * @param watchlistId - Source list
 * @param listName - Source list name
 * @param playerIds - The new slice, in order; empty drops the list
 * @returns The next selection.
 */
export const setListSelection = (
  state: SelectionState,
  watchlistId: string,
  listName: string,
  playerIds: readonly string[]
): SelectionState =>
  withSlice(state, watchlistId, listName, [...new Set(playerIds)]);

/**
 * Drops one list's slice entirely (picker untick, Undo on a deleted source).
 * @param state - Live selection
 * @param watchlistId - Source list
 * @returns The next selection.
 */
export const dropList = (
  state: SelectionState,
  watchlistId: string
): SelectionState => ({
  slices: state.slices.filter(slice => slice.watchlistId !== watchlistId),
});

/**
 * Removes player ids from every slice (after a bulk remove or a copy that
 * should not leave stale ids behind).
 * @param state - Live selection
 * @param playerIds - Ids to drop everywhere
 * @returns The next selection.
 */
export const dropPlayers = (
  state: SelectionState,
  playerIds: readonly string[]
): SelectionState => {
  const gone = new Set(playerIds);
  return {
    slices: state.slices
      .map(slice => ({
        ...slice,
        playerIds: slice.playerIds.filter(id => !gone.has(id)),
      }))
      .filter(slice => slice.playerIds.length > 0),
  };
};

/**
 * Every selected player once, first source first (PRD-3.10).
 * @param state - Live selection
 * @returns Unique player ids in encounter order.
 */
export const uniquePlayerIds = (state: SelectionState): readonly string[] => [
  ...new Set(state.slices.flatMap(slice => slice.playerIds)),
];

/**
 * Sum of every slice, duplicates included.
 * @param state - Live selection
 * @returns The raw selected count.
 */
export const totalSelected = (state: SelectionState): number =>
  state.slices.reduce((sum, slice) => sum + slice.playerIds.length, 0);

/**
 * Header checkbox state for the rows on screen.
 * @param selected - Selected ids in the active list
 * @param visiblePlayerIds - Rows currently rendered
 * @returns none / some / all.
 */
export const headerStateFor = (
  selected: readonly string[],
  visiblePlayerIds: readonly string[]
): SelectionHeaderState => {
  if (visiblePlayerIds.length === 0) return "none";
  const picked = new Set(selected);
  const count = visiblePlayerIds.filter(id => picked.has(id)).length;
  if (count === 0) return "none";
  return count === visiblePlayerIds.length ? "all" : "some";
};

/** Inputs for {@link selectionBarCopy}. */
export interface SelectionBarCopyInput {
  readonly state: SelectionState;
  /** The list on screen (null on All Targets). */
  readonly activeWatchlistId: string | null;
  /** Abbreviated position filter label, e.g. "CB", when select-all was filtered. */
  readonly filterLabel: string | null;
}

/**
 * The selection bar's sentence (PRD-3.7, 3.8, 3.10).
 * @param input - Live selection, active list and any filter label
 * @param input.state - Live selection
 * @param input.activeWatchlistId - The list on screen
 * @param input.filterLabel - Abbreviated position filter label, if any
 * @returns e.g. "2 selected here (filter: CB)" or
 * "9 selected here · 30 across 2 lists (A 21 · B 9) · 28 unique".
 */
export const selectionBarCopy = ({
  state,
  activeWatchlistId,
  filterLabel,
}: SelectionBarCopyInput): string => {
  const here = activeWatchlistId ? selectedIn(state, activeWatchlistId) : [];
  const total = totalSelected(state);
  const unique = uniquePlayerIds(state).length;
  const lists = state.slices.length;
  const filterSuffix = filterLabel ? ` (filter: ${filterLabel})` : "";
  if (lists === 0 || (here.length > 0 && lists === 1)) {
    return `${here.length} selected here${filterSuffix}`;
  }
  if (lists === 1) {
    const [slice] = state.slices;
    return `0 selected here${filterSuffix} · ${total} in ${slice.listName}`;
  }
  const breakdown = state.slices
    .map(slice => `${slice.listName} ${slice.playerIds.length}`)
    .join(" · ");
  const uniqueSuffix = unique < total ? ` · ${unique} unique` : "";
  return `${here.length} selected here${filterSuffix} · ${total} across ${lists} lists (${breakdown})${uniqueSuffix}`;
};

/**
 * Copies selected count per list for the pills ("✓ 21").
 * @param state - Live selection
 * @returns Map of watchlist id → selected count.
 */
export const selectionCountsByList = (
  state: SelectionState
): Readonly<Record<string, number>> =>
  Object.fromEntries(
    state.slices.map(slice => [slice.watchlistId, slice.playerIds.length])
  );

/** How the carried slots landed (SE-8357, R04). */
export interface PlacementSplit {
  /** Players written at their first-source slot. */
  readonly placed: number;
  /** Players appended Unassigned: slot missing from the formation or taken. */
  readonly unassigned: number;
}

/**
 * Splits the payload's `placementOutcomes` for the toast (SE-8357). Wire
 * strings, not the generated enum: this runs under suites that partially
 * mock the generated module. `ALREADY_PRESENT` is neither — it stays in the
 * "already there" count the id partitions carry.
 * @param outcomes - The payload's placement answers
 * @returns Placed and Unassigned counts.
 */
export const placementSplit = (
  outcomes: readonly { readonly outcome: string }[]
): PlacementSplit => ({
  placed: outcomes.filter(entry => entry.outcome === "PLACED").length,
  unassigned: outcomes.filter(
    entry =>
      entry.outcome === "SLOT_NOT_IN_FORMATION" ||
      entry.outcome === "SLOT_TAKEN"
  ).length,
});

/**
 * Toast copy after a bulk copy (PRD-3.10). With placements carried
 * (SE-8357), the backend's split follows the copied count; a split with
 * nothing placed and nothing unassigned (no slot was requested, or every
 * request answered already-present) says nothing.
 * @param added - Ids the backend inserted
 * @param alreadyPresent - Ids the destination already held
 * @param split - How the carried slots landed, if any were carried
 * @returns e.g. "9 copied · 7 placed · 2 unassigned · 3 were already there".
 */
export const copyToastCopy = (
  added: readonly string[],
  alreadyPresent: readonly string[],
  split?: PlacementSplit
): string =>
  [
    `${added.length} copied`,
    ...(split && split.placed + split.unassigned > 0
      ? [`${split.placed} placed`, `${split.unassigned} unassigned`]
      : []),
    ...(alreadyPresent.length > 0
      ? [`${alreadyPresent.length} were already there`]
      : []),
  ].join(" · ");

/**
 * "A and B" / "A, B and C" for prose (PRD-3.9 helper and callout).
 * @param names - Source list names in encounter order
 * @returns The joined names.
 */
const joinNames = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/**
 * Name-it prefill (SE-8335, PRD-3.9): "{list} · top N" for one source, taken
 * literally for any single-source selection; "{A} + {B}" for several.
 * @param sourceNames - Source list names in encounter order
 * @param uniqueCount - Unique selected players
 * @returns The prefilled name.
 */
export const newListNamePrefill = (
  sourceNames: readonly string[],
  uniqueCount: number
): string =>
  sourceNames.length <= 1
    ? `${sourceNames[0] ?? ""} · top ${uniqueCount}`
    : sourceNames.join(" + ");

/**
 * Name-it helper (PRD-3.9): "N players copied from {list}".
 * @param uniqueCount - Unique selected players
 * @param sourceNames - Source list names in encounter order
 * @returns The helper line.
 */
export const newListHelperCopy = (
  uniqueCount: number,
  sourceNames: readonly string[]
): string =>
  `${uniqueCount} ${uniqueCount === 1 ? "player" : "players"} copied from ${joinNames(sourceNames)}`;

/**
 * Name-it callout (PRD-3.9, FC04 frame text): the copy is independent; with
 * several sources the order sentence says whose order comes first.
 * @param sourceNames - Source list names in encounter order
 * @returns The callout sentence(s).
 */
export const newListCalloutCopy = (sourceNames: readonly string[]): string => {
  const base =
    "This creates a separate list, copied now. Adding or removing players here won’t change";
  if (sourceNames.length <= 1) return `${base} ${sourceNames[0] ?? ""}.`;
  const order = `Order: ${sourceNames[0]} first, then ${joinNames(sourceNames.slice(1))} — re-rank inside the new list.`;
  return `${base} ${sourceNames.slice(0, -1).join(", ")} or ${sourceNames[sourceNames.length - 1]}. ${order}`;
};

/**
 * Lists the viewer may copy into (SE-8334, PV08): a viewer-only grant never
 * writes through the copy shortcut.
 *
 * The writable set itself lives in `@/features/watchlist/constants` (SE-8306)
 * so the copy shortcut here and the player-page membership checkbox cannot
 * drift apart about which grants the backend actually accepts a write from.
 * @param lists - Candidate destinations with the viewer's grant
 * @returns The OWNER / EDITOR lists, in the given order.
 */
export const writableDestinations = <
  T extends { readonly role: UserWatchlistRole },
>(
  lists: readonly T[]
): readonly T[] => lists.filter(list => canEditWatchlistMembership(list.role));

/**
 * What the picker says when a copy into one destination fails (SE-8334).
 * @param listName - The destination's name
 * @param gone - Whether the backend answered not-found (deleted or unshared)
 * @returns The inline failure copy.
 */
export const copyFailureCopy = (listName: string, gone: boolean): string =>
  gone
    ? `${listName} is no longer shared with you`
    : `Copy to ${listName} failed · try again`;
